import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Wallet, ArrowDownToLine, Clock, IndianRupee, X, Calendar, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function AdminAgentEarnings() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("commissions");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showPay, setShowPay] = useState(false);

  const load = () => api.get(`/admin/agents/${id}/earnings`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  const inRange = (iso) => {
    if (!from && !to) return true;
    const t = new Date(iso).getTime();
    if (from && t < new Date(from).getTime()) return false;
    if (to && t > new Date(to).getTime() + 24 * 3600 * 1000) return false;
    return true;
  };

  const collections = useMemo(() => (data?.collections || []).filter((c) => inRange(c.created_at)), [data, from, to]);
  const payments = useMemo(() => (data?.payments || []).filter((p) => inRange(p.created_at)), [data, from, to]);

  if (!data) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  return (
    <div data-testid="admin-agent-earnings-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="back-btn" onClick={() => nav("/admin/agents")} className="inline-flex items-center gap-1 text-sm text-slate-600 font-bold">
          <ArrowLeft className="w-4 h-4" /> Back to Agents
        </button>
        <div className="flex-1" />
        <button data-testid="open-pay-agent" onClick={() => setShowPay(true)} className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-bold tap-scale">
          <ArrowDownToLine className="w-4 h-4" /> Pay Agent
        </button>
      </div>

      <div className="mt-4 bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">{data.agent.full_name}</h1>
            <div className="text-sm text-slate-500">+91 {data.agent.phone} · {data.agent.agent_code} · Commission {data.agent.commission_rate}%</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card label="Total Earned" value={fmtINR(data.total_earned)} icon={Wallet} grad="emerald-grad" testId="ae-total-earned" />
          <Card label="Total Paid" value={fmtINR(data.total_paid)} icon={ArrowDownToLine} grad="bg-tenant-gradient" testId="ae-total-paid" />
          <Card label="Balance Due" value={fmtINR(data.balance_due)} icon={Clock} grad="bg-amber-500" testId="ae-balance" />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="flex bg-white border border-slate-200 rounded-xl p-1">
          <button data-testid="tab-commissions" onClick={() => setTab("commissions")} className={`px-3 py-1.5 text-[11px] font-bold rounded-lg uppercase tracking-wider ${tab === "commissions" ? "bg-tenant-primary text-white" : "text-slate-500"}`}>
            Commissions ({collections.length})
          </button>
          <button data-testid="tab-payments" onClick={() => setTab("payments")} className={`px-3 py-1.5 text-[11px] font-bold rounded-lg uppercase tracking-wider ${tab === "payments" ? "bg-tenant-primary text-white" : "text-slate-500"}`}>
            Payments ({payments.length})
          </button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <DateInput value={from} onChange={setFrom} testId="ae-from" />
          <DateInput value={to} onChange={setTo} testId="ae-to" />
          {(from || to) && <button data-testid="clear-filter" onClick={() => { setFrom(""); setTo(""); }} className="text-[11px] font-bold text-slate-500 underline">Clear</button>}
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-100 rounded-2xl card-shadow table-scroll">
        {tab === "commissions" ? (
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Customer</th>
                <th className="text-left p-3">Method</th>
                <th className="text-right p-3">Collection</th>
                <th className="text-right p-3">Commission</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {collections.map((c) => (
                <tr key={c.id} data-testid={`ae-row-${c.id}`} className="hover:bg-slate-50">
                  <td className="p-3 text-[12px]">{fmtDate(c.created_at)}</td>
                  <td className="p-3">
                    <div className="font-bold text-[13px]">{c.customer_name || "—"}</div>
                    <div className="text-[10px] text-slate-400">+91 {c.customer_phone}</div>
                  </td>
                  <td className="p-3 text-[11px] uppercase tracking-wider text-slate-500">{c.method}</td>
                  <td className="p-3 text-right font-bold">{fmtINR(c.collection_amount)}</td>
                  <td className="p-3 text-right font-extrabold text-emerald-600">+{fmtINR(c.commission)}</td>
                </tr>
              ))}
              {collections.length === 0 && <tr><td colSpan={5} className="text-center p-6 text-slate-400">No commission entries in range.</td></tr>}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Method</th>
                <th className="text-left p-3">Remarks</th>
                <th className="text-right p-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => (
                <tr key={p.id} data-testid={`ap-row-${p.id}`} className="hover:bg-slate-50">
                  <td className="p-3 text-[12px]">{fmtDate(p.created_at)}</td>
                  <td className="p-3 text-[11px] uppercase tracking-wider text-slate-500">{p.method}</td>
                  <td className="p-3 text-[12px] text-slate-600">{p.remarks || "—"}</td>
                  <td className="p-3 text-right font-extrabold text-blue-600">{fmtINR(p.amount)}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={4} className="text-center p-6 text-slate-400">No payments yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {showPay && (
        <PayModal balance={data.balance_due} agentId={id} onClose={() => setShowPay(false)} onDone={() => { setShowPay(false); load(); }} />
      )}
    </div>
  );
}

const Card = ({ label, value, icon: Icon, grad, testId }) => (
  <div className={`${grad} text-white rounded-2xl p-5 card-shadow`}>
    <Icon className="w-6 h-6" />
    <div className="text-[10px] uppercase tracking-[0.25em] opacity-90 font-bold mt-2">{label}</div>
    <div data-testid={testId} className="text-2xl font-extrabold tracking-tight mt-1">{value}</div>
  </div>
);

const DateInput = ({ value, onChange, testId }) => (
  <div className="relative">
    <Calendar className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
    <input data-testid={testId} type="date" value={value} onChange={(e) => onChange(e.target.value)} className="pl-6 pr-2 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none focus:border-tenant-primary" />
  </div>
);

const PayModal = ({ balance, agentId, onClose, onDone }) => {
  const [amount, setAmount] = useState(String(balance || ""));
  const [method, setMethod] = useState("cash");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > balance + 0.01) return toast.error(`Cannot exceed balance ${fmtINR(balance)}`);
    setSaving(true);
    try {
      await api.post(`/admin/agents/${agentId}/pay`, { amount: amt, method, remarks });
      toast.success(`Paid ${fmtINR(amt)} to agent`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
      <div data-testid="pay-agent-modal" className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-extrabold text-lg">Pay Agent</div>
            <div className="text-[11px] text-slate-500">Balance due: <b className="text-amber-700">{fmtINR(balance)}</b></div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount</label>
        <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-3 py-2.5">
          <IndianRupee className="w-4 h-4 text-slate-400" />
          <input data-testid="pay-amount" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} className="flex-1 outline-none font-extrabold" />
          <button onClick={() => setAmount(String(balance))} className="text-[10px] bg-slate-100 font-bold px-2 py-1 rounded">FULL</button>
        </div>

        <label className="mt-3 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Method</label>
        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
          {["cash", "upi", "bank", "gpay", "phonepe"].map((m) => (
            <button key={m} data-testid={`pay-method-${m}`} onClick={() => setMethod(m)} className={`py-2 rounded-xl text-[11px] font-bold uppercase ${method === m ? "bg-tenant-primary text-white" : "bg-slate-100"}`}>
              {m}
            </button>
          ))}
        </div>

        <label className="mt-3 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Remarks</label>
        <input data-testid="pay-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" placeholder="(optional)" />

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="py-2.5 rounded-xl border border-slate-200 font-bold">Cancel</button>
          <button data-testid="confirm-pay" onClick={submit} disabled={saving} className="py-2.5 rounded-xl bg-emerald-600 text-white font-bold disabled:opacity-60">
            {saving ? "Saving..." : "Pay Now"}
          </button>
        </div>
      </div>
    </div>
  );
};
